import { useCallback, useState } from 'react';

import { ApiFailure, request } from '../../shared/api';
import { countFilters, type Filters } from '../../shared/filters';
import { matches, rate, warning, type Preview } from '../../shared/preview';
import { LIMIT_CODES, LimitState } from '../../shared/states';
import { NAME_LIMIT, subscriptionName, type Chosen } from '../../shared/subscription-name';
import { useUpgrade } from '../../shared/upgrade';

/**
 * Подписка из фильтров (T11.7, `09_UI_UX.md` §4.3).
 *
 * Кнопка постоянная, как и требует §4.3: это шаг, превращающий
 * посетителя в пользователя, и прятать его за состоянием экрана значит
 * показывать его тем, кто и так дошёл до конца.
 *
 * ## Три действия — это потолок, а не цель
 *
 * Задача требует пути от фильтра до работающей подписки не более чем за
 * три действия. Здесь их два: «Подписаться» раскрывает карточку,
 * «Создать» её сохраняет. Третье — правка имени — не обязательно, потому
 * что имя уже предложено (`shared/subscription-name.ts`).
 *
 * Отдельного экрана нет намеренно. Переход уводит от фильтров, а
 * подписка — это и есть фильтры: человек должен видеть, на что
 * подписывается, в тот самый момент, когда решает.
 *
 * ## Предпросмотр советует, но не запрещает
 *
 * `POST /v1/subscriptions/preview` (T7.10) отвечает на два вопроса,
 * невидимых в момент создания: сколько подходит сейчас и сколько будет
 * приходить в сутки. Слишком широкий фильтр зальёт уведомлениями,
 * слишком узкий не сработает никогда — и обе ошибки тихие.
 *
 * Но предпросмотр не условие сохранения. Он идёт вторым запросом, и его
 * отказ не должен отнимать возможность подписаться: карточка
 * открывается сразу, а числа проступают, когда придут. Молчание
 * советчика — не повод не давать сделать то, за чем пришли.
 *
 * ## Пустой фильтр не предлагается
 *
 * Подписка без единого условия подходит под всё и выберет суточную квоту
 * за первый же час, после чего человек отключит уведомления и уйдёт. Это
 * единственный случай, когда кнопка недоступна.
 */

interface Created {
  readonly id: string | number;
  readonly name: string;
}

type Stage =
  | { readonly kind: 'idle' }
  | { readonly kind: 'open' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'done'; readonly name: string }
  | { readonly kind: 'limited'; readonly hint: string }
  | { readonly kind: 'failed'; readonly hint: string };

export function SubscribeCard({
  filters,
  chosen,
  onCreated,
}: {
  filters: Filters;
  chosen: Chosen;
  /** Радар считает подписки, и о новой он обязан узнать. */
  onCreated?: (created: Created) => void;
}): React.JSX.Element {
  const onUpgrade = useUpgrade();
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const [name, setName] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);

  const open = useCallback(() => {
    setName(subscriptionName(filters, chosen));
    setPreview(null);
    setStage({ kind: 'open' });

    request<Preview>('/v1/subscriptions/preview', { method: 'POST', body: { filters } })
      .then(setPreview)
      .catch(() => {
        // Совет не пришёл — создавать это не мешает
        setPreview(null);
      });
  }, [chosen, filters]);

  const create = useCallback(() => {
    setStage({ kind: 'saving' });

    request<Created>('/v1/subscriptions', {
      method: 'POST',
      body: { name: name.trim(), filters },
    })
      .then((created) => {
        setStage({ kind: 'done', name: created.name });
        onCreated?.(created);
      })
      .catch((failure: unknown) => {
        if (failure instanceof ApiFailure && LIMIT_CODES.has(failure.code)) {
          setStage({ kind: 'limited', hint: failure.message });

          return;
        }

        setStage({
          kind: 'failed',
          hint:
            failure instanceof ApiFailure
              ? failure.message
              : 'Сервис не ответил. Это бывает; попробуйте ещё раз.',
        });
      });
  }, [filters, name, onCreated]);

  if (stage.kind === 'done') {
    return (
      <div className="card subscribe">
        <p className="subscribe__title">Подписка создана</p>
        <p className="subscribe__hint">
          «{stage.name}» уже работает. Как только появится подходящая машина, пришлю карточку в чат.
        </p>
        <button
          type="button"
          className="button"
          onClick={() => {
            setStage({ kind: 'idle' });
          }}
        >
          Готово
        </button>
      </div>
    );
  }

  if (stage.kind === 'limited') {
    return (
      <div className="card subscribe">
        <LimitState title="Достигнут предел тарифа" hint={stage.hint} onUpgrade={onUpgrade} />
      </div>
    );
  }

  if (stage.kind === 'idle') {
    const count = countFilters(filters);

    return (
      <div className="field">
        <button
          type="button"
          className="button button--accent"
          disabled={count === 0}
          onClick={open}
        >
          {count === 0 ? 'Задайте условия, чтобы подписаться' : 'Подписаться на этот поиск'}
        </button>
      </div>
    );
  }

  const note = preview === null ? null : warning(preview);

  return (
    <div className="card subscribe">
      <p className="subscribe__title">Новая подписка</p>

      <label className="field">
        <span className="field__title">Название</span>
        <input
          className="input"
          value={name}
          maxLength={NAME_LIMIT}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
      </label>

      {/*
        Строка с числами стоит на месте и до их прихода: появившаяся
        снизу, она сдвинула бы кнопку под пальцем ровно в момент нажатия.
      */}
      <p className="subscribe__hint">
        {preview === null
          ? 'Считаю, сколько таких объявлений…'
          : `${matches(preview)} · ${rate(preview)}`}
      </p>

      {note === null ? null : <p className="subscribe__warning">{note}</p>}
      {stage.kind === 'failed' ? <p className="subscribe__warning">{stage.hint}</p> : null}

      <div className="subscribe__actions">
        <button
          type="button"
          className="button button--accent"
          disabled={stage.kind === 'saving' || name.trim() === ''}
          onClick={create}
        >
          {stage.kind === 'saving' ? 'Создаю…' : 'Создать'}
        </button>
        <button
          type="button"
          className="button"
          onClick={() => {
            setStage({ kind: 'idle' });
          }}
        >
          Отмена
        </button>
      </div>
    </div>
  );
}
